import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import config from '../../config';

const mapStyle = [
  {
    featureType: 'administrative',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'labels.text',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        lightness: '0',
      },
    ],
  },
  {
    featureType: 'poi.medical',
    elementType: 'geometry.fill',
    stylers: [
      {
        lightness: '-5',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [
      {
        visibility: 'on',
      },
      {
        color: '#A7CE95',
      },
      {
        lightness: '45',
      },
    ],
  },
  {
    featureType: 'poi.school',
    elementType: 'geometry',
    stylers: [
      {
        color: '#BE9B7B',
      },
      {
        lightness: '70',
      },
    ],
  },
  {
    featureType: 'poi.sports_complex',
    elementType: 'geometry',
    stylers: [
      {
        color: '#5D4B46',
      },
      {
        lightness: '60',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        visibility: 'on',
      },
      {
        color: '#FFFFFF',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'on',
      },
      {
        color: '#DEDEDE',
      },
    ],
  },
  {
    featureType: 'transit.station',
    elementType: 'geometry',
    stylers: [
      {
        saturation: '23',
      },
      {
        lightness: '10',
      },
      {
        gamma: '0.8',
      },
      {
        hue: '#B000FF',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#A2DAF2',
      },
    ],
  },
];

// Shared by every map page, deliberately NOT a prop.
//
// `useJsApiLoader` below is called with a fixed `id: 'googleapis'`, and
// `@react-google-maps/api` warns — and can throw "Loader must not be called
// again with different options" — when one loader id is used with different
// `libraries` arrays in the same session. A `libraries` prop would invite
// exactly that error the moment two map pages mounted with different sets, so
// every map page loads the same one.
//
// `places` is required: `EditRideLocationDialog` constructs
// `new window.google.maps.places.Autocomplete`, which is a TypeError without it.
//
// DO NOT ADD `drawing`. It was here briefly for the station builder's rectangle
// select, and `DrawingManager` was REMOVED in Maps JavaScript API 3.65 —
// constructing one throws, taking the page blank. `AreaSelect` now uses a core
// editable `Rectangle` and needs nothing from `drawing`; loading a library whose
// only member throws would just advertise a capability that no longer exists.
const LIBRARIES = ['places'];

const Map = ({ children, options = {}, ...props }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'googleapis',
    googleMapsApiKey: config.googleapis.apiKey,
    libraries: LIBRARIES,
  });

  if (!isLoaded) return <></>;

  return (
    // This wrapper must carry an explicit size. Every caller passes
    // `mapContainerStyle={{ width: '100%', height: '100%' }}`, and a percentage
    // height resolves against the PARENT — so with an auto-height wrapper the
    // chain is circular (wrapper sizes to its content, content is 100% of the
    // wrapper) and both collapse to 0. The map then mounts but renders as a
    // zero-height strip, i.e. an invisible map on a page that otherwise looks
    // fine. Sizing the wrapper to fill its own flex parent breaks the cycle.
    <div style={{ width: '100%', height: '100%' }}>
      <GoogleMap
        {...props}
        options={{
          fullscreenControl: true,
          streetViewControl: true,
          zoomControl: false,
          mapTypeControl: true,
          styles: mapStyle,
          ...options,
        }}
      >
        {children}
      </GoogleMap>
    </div>
  );
};

export default Map;
